/**
 * The clients list's at-a-glance cells: citizenship, status with its end date,
 * next deadline, portal state and the possible-duplicate flag.
 */
import dayjs from "dayjs";
import { Copy } from "lucide-react";
import type { FC } from "react";

import type { ClientListMeta } from "@/api/types/clientDirectory";
import type { MatterDTO } from "@/api/types/matter";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getWorkflowType, WorkflowType } from "@/types/workflow.type";

type Tone = "red" | "amber" | "emerald" | "neutral";

const TONE: Record<Tone, StatusTone> = {
  red: "danger",
  amber: "warn",
  emerald: "success",
  neutral: "neutral"
};

const Muted: FC<{ children: string }> = ({ children }) => (
  <span className="text-sm text-fg-3">{children}</span>
);

const day = (iso: string) => dayjs(iso.slice(0, 10));
const ago = (n: number) => `${n} day${n === 1 ? "" : "s"}`;

/** "H-1B" over "Ended 101 days ago" / "Ends in 47 days" / "Until Jun 2027". */
export const statusChip = (
  status: ClientListMeta["status"]
): { text: string; tone: Tone } | null => {
  const left = status.days_left;
  switch (status.state) {
    case "expired":
      return { text: `Ended ${ago(Math.abs(left ?? 0))} ago`, tone: "red" };
    case "expiring":
      return { text: `Ends in ${ago(left ?? 0)}`, tone: "amber" };
    case "unknown":
      return { text: "End date missing", tone: "neutral" };
    case "current":
      return status.ends
        ? {
            text: `Until ${day(status.ends).format("MMM YYYY")}`,
            tone: "emerald"
          }
        : status.duration_of_status
        ? { text: "D/S", tone: "emerald" }
        : null;
    default:
      return null;
  }
};

/** The status column has something to show for this client. */
export const hasStatus = (meta?: ClientListMeta): boolean =>
  Boolean(meta?.status.status);

/** The next-deadline column has something to show for this client. */
export const hasDeadline = (meta?: ClientListMeta): boolean =>
  Boolean(meta?.next_deadline);

export const StatusCell: FC<{ meta?: ClientListMeta }> = ({ meta }) => {
  if (!meta?.status.status) return <Muted>—</Muted>;
  const chip = statusChip(meta.status);
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="text-base text-fg">{meta.status.status}</span>
      {chip ? (
        <StatusBadge tone={TONE[chip.tone]} data-testid="client-status-chip">
          {chip.text}
        </StatusBadge>
      ) : null}
    </div>
  );
};

export const CitizenshipCell: FC<{ meta?: ClientListMeta }> = ({ meta }) => {
  const list = meta?.citizenships ?? [];
  if (list.length === 0) return <Muted>—</Muted>;
  return <Muted>{list.join(", ")}</Muted>;
};

type Deadline = NonNullable<ClientListMeta["next_deadline"]>;

const DEADLINE_TONE: Record<Tone, string> = {
  red: "text-danger",
  amber: "text-warn",
  emerald: "text-success",
  neutral: "text-fg-3"
};

/** "3 days overdue" / "Due today" / "In 12 days". */
export const deadlineCountdown = (
  d: Deadline
): { text: string; tone: Tone } => ({
  tone: d.days_left < 0 ? "red" : d.days_left <= 14 ? "amber" : "neutral",
  text:
    d.days_left < 0
      ? `${ago(-d.days_left)} overdue`
      : d.days_left === 0
      ? "Due today"
      : `In ${ago(d.days_left)}`
});

/** What the deadline is for: a notice response or the filing itself. Null
 * until the client's matters load. */
export const deadlineLabel = (
  d: Deadline,
  matters?: MatterDTO[]
): string | null => {
  const matter = matters?.find((m) => m.individual_id === d.individual_id);
  if (!matter) return null;
  const ind = matter.individual as unknown as {
    is_rfe?: unknown;
    workflow_type?: string | null;
  } | null;
  switch (getWorkflowType(ind?.workflow_type, Boolean(ind?.is_rfe))) {
    case WorkflowType.RFE:
      return "RFE Response";
    case WorkflowType.NOIR_NOID:
      return "NOID/NOIR Response";
    default:
      return d.visa ? `${d.visa} Filing` : "Filing";
  }
};

/** "Sat, Oct 10" this year, "Jan 4, 2027" otherwise. */
export const deadlineDate = (d: Deadline): string => {
  const due = day(d.due);
  return due.format(
    due.year() === dayjs().year() ? "ddd, MMM D" : "MMM D, YYYY"
  );
};

export const deadlineToneClass = (d: Deadline): string =>
  DEADLINE_TONE[deadlineCountdown(d).tone];

/** "Sat, Oct 10" over "3 days overdue · RFE Response". */
export const DeadlineCell: FC<{
  meta?: ClientListMeta;
  matters?: MatterDTO[];
}> = ({ meta, matters }) => {
  const d = meta?.next_deadline;
  if (!d) return <Muted>—</Muted>;
  const label = deadlineLabel(d, matters);
  return (
    <div className="flex flex-col gap-0.5" data-testid="client-deadline">
      <span className="text-base whitespace-nowrap text-fg tabular-nums">
        {deadlineDate(d)}
      </span>
      <span className={cn("text-sm whitespace-nowrap", deadlineToneClass(d))}>
        {deadlineCountdown(d).text}
        {label ? <span className="text-fg-4">{` · ${label}`}</span> : null}
      </span>
    </div>
  );
};

const PORTAL: Record<
  ClientListMeta["portal"]["state"],
  { text: string; on: boolean }
> = {
  active: { text: "Active", on: true },
  off: { text: "Not Invited", on: false },
  none: { text: "No Login", on: false },
  unknown: { text: "—", on: false }
};

/** The portal state as a dot and a word ("● Active"). */
export const PortalState: FC<{ meta: ClientListMeta }> = ({ meta }) => {
  const p = PORTAL[meta.portal.state];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-sm whitespace-nowrap",
        p.on ? "text-fg-2" : "text-fg-3"
      )}
      data-testid="client-portal-state"
    >
      <span
        aria-hidden
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          p.on ? "bg-success" : "bg-fg-4"
        )}
      />
      {p.text}
    </span>
  );
};

export const PortalCell: FC<{ meta?: ClientListMeta }> = ({ meta }) => {
  if (!meta || meta.portal.state === "unknown") return <Muted>—</Muted>;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <PortalState meta={meta} />
      {meta.portal.state === "active" && meta.portal.last_sign_in ? (
        <span className="pl-3 text-sm text-fg-4">
          {`Seen ${dayjs(meta.portal.last_sign_in).format("MMM D")}`}
        </span>
      ) : null}
    </div>
  );
};

/** A small amber copy icon after the name; the tooltip names the matches. */
export const DuplicateFlag: FC<{
  meta?: ClientListMeta;
  onReview: (otherId: string) => void;
}> = ({ meta, onReview }) => {
  const dupes = meta?.possible_duplicates ?? [];
  if (dupes.length === 0) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex size-4 shrink-0 items-center justify-center rounded-xs text-warn outline-none hover:bg-warn-soft focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label="Possible Duplicate"
          data-testid="client-duplicate-flag"
          onClick={(e) => {
            e.stopPropagation();
            onReview(dupes[0].foreign_national_id);
          }}
        >
          <Copy className="size-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        Possible duplicate of{" "}
        {dupes.map((d) => `${d.name} (${d.reason.toLowerCase()})`).join(", ")}.
        Click to compare and merge.
      </TooltipContent>
    </Tooltip>
  );
};
